<template>
    <Modal
        :open="display"
        title="Add Content to Request"
        size="large"
        acceptButtonText="Add Content"
        :showEscapeButton="true"
        @close="notifyCloseModal()"
    >
        <div class="flex flex-row space-x-4">
            <div class="space-y-1 flex-1">
                <label
                    for="searchText"
                    class="block text-sm font-medium text-gray-700"
                    >Search Query
                </label>
                <input
                    type="text"
                    name="searchText"
                    id="searchText"
                    placeholder="Search by a title, keyword, tag, or YouTube ID..."
                    class="focus:ring-blue-500 focus:border-blue-500 block w-full pl-4 pr-4 sm:text-sm border-gray-300 rounded-md shadow-sm "
                    v-model="searchQuery"
                />
            </div>
            <div class="space-y-1" v-if="isAdmin">
                <label
                    for="searchText"
                    class="block text-sm font-medium text-gray-700"
                    >User
                </label>
                <select
                    class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    v-model="userId"
                >
                    <option :value="null">All</option>
                    <option
                    v-for="stringer in stringers"
                    :value="stringer.id"
                    v-bind:key="stringer.id"
                    >{{ stringer.name }}</option
                    >
                </select>
            </div>
        </div>
        
        <div class="overflow-y-scroll -m-2 mt-2" style="max-height: 600px;">
            <div class="flex flex-col space-y-4 p-2">
                <div
                    v-for="video in videos"
                    class="flex flex-row border rounded-md border-2 p-2 ring-offset-2 cursor-pointer flex flex-row"
                    :class="{'ring-2 ring-blue-400': selectedVideoIds.includes(video.id)}"
                    v-on:click="toggleVideo(video)"
                >
                    <div class="flex-none flex flex-col items-center content-center w-32 relative">
                        <div
                            class="absolute top-0 left-0 mt-2 ml-2 bg-blue-500 rounded-md text-white z-20 text-xs px-1 py-0.25"
                        >
                            {{ video.length | timecode }}
                        </div>
                        <progressive-img
                            class="rounded-md z-0"
                            fallback="/static/image-unavailable.png"
                            :src="video.thumbnailUrl"
                        />
                    </div>
                    <div class="flex-1 flex flex-col pl-2 whitespace-nowrap overflow-ellipsis overflow-hidden relative">
                        <p class="whitespace-nowrap overflow-ellipsis overflow-hidden font-medium" v-tippy :title="video.title">{{ video.title }}</p>
                        {{ video.created_at | displayLocalDateTime }} &middot; {{ video.user.name }}<br />
                    </div>   
                </div>
                <div
                    ref="loadMoreDiv"
                    class="h-10 flex items-center content-center justify-center"
                >
                    <button
                        class="px-3 py-1.5 bg-gray-300 rounded-md focus:outline-none hover:bg-gray-400"
                        v-on:click="getRecentContent"
                        v-if="!searching"
                    >
                        Load more
                    </button>
                </div>
            </div>
        </div>
    </Modal>
</template>

<script>
    import * as Config from '@/config'
    import { mapState, mapGetters } from 'vuex'

    import Modal from '@/components/Modal'

    let stringerApiUrl = Config.apiPath + "/api/v2/stringer/videos?status=approved&orderBy=desc";
    let adminApiUrl = Config.apiPath + "/api/v2/admin/videos?status=approved&orderBy=desc";

    let stringerUpdateRequestApiUrl = Config.apiPath + "/api/v2/stringer/requests/";
    let adminUpdateRequestApiUrl = Config.apiPath + "/api/v2/admin/networks/requests/";

    export default {
        props: {
            display: {
                type: Boolean,
                default: false
            },
            selectorVersion: {
                type: String,
                default: 'stringer'
            },
            currentlySelectedVideoIds: {
                type: Array,
                default: []
            }
        },
        data() {
            return {
                displayModal: false,
                data: null,
                videos: [],
                paginationInfo: {
                    current_page: 0,
                    last_page: null,
                },
                originalSelectedVideoIds: [],
                selectedVideoIds: [],
                apiUrl: stringerApiUrl,
                updateRequestApiUrl: stringerUpdateRequestApiUrl,
                searchQuery: null,
                stringers: [],
                userId: null,
                searching: false,
                bottom: false,
                isAdmin: false,
                removedVideos: [],
                addedVideos: [],
            }
        },

        computed: {
            ...mapGetters(["userInfo"])
        },

        mounted() {
            this.selectedVideoIds = this.currentlySelectedVideoIds
            let role = this.userInfo.role
            if(this.userInfo !== null && role.includes('admin') && this.selectorVersion !== 'stringer') {
                this.isAdmin = true;
                this.apiUrl = adminApiUrl;
                this.updateRequestApiUrl = adminUpdateRequestApiUrl;
            }
        },

        created() {
            this.$nextTick(async () => {
                this.removedVideos = [];
                this.addedVideos = [];
                await this.getStringers();
                await this.getRecentContent();
                this.handleScroll();

                this.$eventBus.$on("scroll", this.bottomVisible);
                this.shouldLoadMore = setInterval(
                    function() {
                        this.bottomVisible();
                    }.bind(this),
                    250
                );
            });
        },

        components: {
            Modal
        },

        watch: {
            display: function(isDisplaying) {
                this.originalSelectedVideoIds = [...this.currentlySelectedVideoIds] // prevents reference from being copied
                this.selectedVideoIds = this.currentlySelectedVideoIds

                if(isDisplaying) {
                    this.paginationInfo.current_page = 0;
                    this.paginationInfo.last_page = 0;
                    this.videos = [];
                    this.getRecentContent()
                }
            },
            searchQuery: function() {
                // DEBUG: console.log('searchQuery changed')
                this.paginationInfo.current_page = 0;
                this.paginationInfo.last_page = 0;
                this.videos = [];
                this.getRecentContent()
            },
            userId: function() {
                // DEBUG: console.log('userId changed')
                this.paginationInfo.current_page = 0;
                this.paginationInfo.last_page = 0;
                this.videos = [];
                this.getRecentContent()
            },
            bottom: function(isAtBottom) {
                // DEBUG: console.log(isAtBottom)
                if (isAtBottom) {
                    this.getRecentContent();
                }
            }
        },

        methods: {
             ...mapGetters([
                'getAuthorizationHeader',
            ]),

            open(data) {
                this.displayModal = true
                this.data = data
            
                this.getRecentContent();
            },

            async getRecentContent() {
                this.searching = true;

                var searchUrl = this.apiUrl;
                searchUrl = this.apiUrl + "&page=" + (this.paginationInfo.current_page + 1);
                if(this.searchQuery !== null) {
                    searchUrl = searchUrl + "&search=" + this.searchQuery;
                }
                if(this.userId !== null) {
                    searchUrl = searchUrl + "&userId=" + this.userId;
                }

                let res = await this.$http.get(searchUrl);
                let data = res.data

                let videos = data.data
                videos.forEach(video => {
                    this.videos.push(video)
                })

                this.paginationInfo.current_page = data.current_page
                this.paginationInfo.last_page = data.last_page
                this.searching = false
            },

            async updateSubmittedContent() {

                // var 

            },

            notifyCloseModal() {
                setTimeout(() => {
                    this.$emit("modalClosed");
                }, 100);
            },

            toggleVideo(video) {
                // Video is not selected
                if(!this.selectedVideoIds.includes(video.id)) {
                    if(this.removedVideos.includes(video.id)) {
                        this.removedVideos.splice(this.removedVideos.indexOf(video.id), 1)
                    }
                    if(!this.addedVideos.includes(video.id) && !this.originalSelectedVideoIds.includes(video.id)) {
                        this.addedVideos.push(video.id)
                    }
                    this.selectedVideoIds.push(video.id)
                } else {
                    // Video was already added, removing
                    if(this.addedVideos.includes(video.id)) {
                        this.addedVideos.splice(this.addedVideos.indexOf(video.id), 1)
                    }
                    console.log(this.originalSelectedVideoIds)
                    if(this.originalSelectedVideoIds.includes(video.id)) {
                        console.log('this.removedVideos.push(video.id)')
                        this.removedVideos.push(video.id)
                    }
                    this.selectedVideoIds.splice(this.selectedVideoIds.indexOf(video.id), 1)
                }
                console.log('originalSelectedVideoIds', this.originalSelectedVideoIds)
                console.log('selectedVideoIds', this.selectedVideoIds)
                console.log('addedVideos', this.addedVideos)
                console.log('removedVideos', this.removedVideos)
            },
            async getStringers() {
                let res = await this.$http.get(
                    Config.apiPath + "/api/v2/admin/users/stringer_list"
                );
                this.$set(this, "stringers", res.data);
            },
            handleScroll() {
                this.bottom = this.bottomVisible();
            },
            bottomVisible() {
                const rect = this.$refs.loadMoreDiv.getBoundingClientRect();

                const isInViewport =
                    rect.top >= 0 &&
                    rect.left >= 0 &&
                    rect.bottom <=
                    (window.innerHeight || document.documentElement.clientHeight) &&
                    rect.right <=
                    (window.innerWidth || document.documentElement.clientWidth);

                this.bottom = isInViewport;

                // DEBUG: console.log('isInViewport', isInViewport)

                return isInViewport;
            },

        },
        
    }
</script>

<style>
    .video-preview {
        text-align: center;
        color: #495057;
    }
</style>